import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  LabelList,
} from "recharts";

import Select from "react-select";
import { getClubList, getClubAnalytics, getLeagueTable } from "../../Services/apis";
import Loader from "../loader";
import { sortByLabel } from "../../lib/sorters";

type ClubOption = { value: number; label: string };

type ClubAnalytics = {
  club_id: number;
  club_name: string;
  hex_code: string;
  secondary_color: string;
  stats: Record<string, number | null>;
};

const colorPalette = ["#E60023", "#6CA0DC", "#FFD700", "#32CD32", "#8A2BE2"];

export default function Linechart() {
  const [selectedTeams, setSelectedTeams] = useState<ClubOption[]>([]);
  const [clubData, setClubData] = useState<ClubOption[]>([]);
  const [analytics, setAnalytics] = useState<ClubAnalytics[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch clubs for dropdown
  const getClubData = async () => {
    try {
      setLoading(true);
      const [clubRes, leagueRes] = await Promise.all([
        getClubList(),
        getLeagueTable("", "", "", 1),
      ]);

      const formatted: ClubOption[] = clubRes.data.map((club: any) => ({
        value: club.club_id,
        label: club.name,
      }));
      const sorted = sortByLabel<ClubOption>(formatted);
      setClubData(sorted);

      // ✅ Preselect rank 1 and rank 2 clubs from the league table
      const leagueRows: any[] = leagueRes.data || [];
      const byRank = [...leagueRows].sort((a, b) => a.rank - b.rank);
      const top2 = byRank.slice(0, 2);
      const defaultSelections: ClubOption[] = top2
        .map((row) => sorted.find((c) => c.value === row.club_id))
        .filter((c): c is ClubOption => c !== undefined);

      if (defaultSelections.length >= 2) {
        setSelectedTeams(defaultSelections);
      } else if (defaultSelections.length === 1) {
        setSelectedTeams(defaultSelections);
      } else if (sorted.length >= 2) {
        // Fallback: first two alphabetically if league table has no rank data
        setSelectedTeams([sorted[0], sorted[1]]);
      } else if (sorted.length > 0) {
        setSelectedTeams([sorted[0]]);
      }
    } catch (err) {
      console.error("Error fetching club list:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics for selected clubs
  const fetchAnalytics = async (teams: ClubOption[]) => {
    if (teams.length === 0) {
      setAnalytics([]);
      return;
    }
    try {
      setLoading(true);
      const clubIds = teams.map((t) => t.value).join(",");
      const res = await getClubAnalytics(clubIds);

      const transformed = res.data.map((club: any) => ({
        club_id: club.club_id,
        club_name: club.club_name,
        hex_code: club.hex_code || "#888888",
        secondary_color: club.secondary_code || "",
        stats: {
          "Top Flight Titles": club.top_flight_titles,
          "European Titles": club.european_titles,
          "FA Cup Titles": club.fa_cup_titles,
          "League Cup Titles": club.league_cup_titles,
          "Financial Value(£)": club.financial_value.toFixed(2),
          "Average Attendance": club.average_attendance,
          "Seasons in Top Division": club.seasons_in_top_division,
          "Trophies (Last 5 Years)": club.trophies_last_5,
          "Country Position": club.league_position ?? 0,
          "Avg Position (Last 5 Years)": club.avg_position_5_years,
          "Followers ": club.followers,
        },
      }));

      setAnalytics(transformed);
    } catch (err) {
      console.error("Error fetching analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getClubData();
  }, []);

  useEffect(() => {
    fetchAnalytics(selectedTeams);
  }, [selectedTeams]);

  // Build chart data
  const statKeys = analytics.length > 0 ? Object.keys(analytics[0].stats) : [];

  const data = statKeys.map((stat) => {
    const entry: any = { stat };
    analytics.forEach((club) => {
      entry[club.club_name] = club.stats[stat];
    });
    return entry;
  });
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div
        style={{
          background: "#a1badd",
          padding: "12px 14px",
          borderRadius: "10px",
          border: "1px solid #d1d5db",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
          minWidth: 120,
        }}
      >
        <p
          style={{
            fontWeight: 600,
            marginBottom: 10,
            fontSize: 14,
            color: "#111",
          }}
        >
          {label}
        </p>

        {payload.map((item: any, index: number) => {
          const club = analytics.find((c) => c.club_name === item.name);
          if (!club) return null;

          return (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              {/* Club name in its real color */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: club.hex_code, // <-- ONLY text color
                  marginRight: 8,
                }}
              >
                {club.club_name} :
              </span>

              {/* Value */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: club.hex_code,
                }}
              >
                {item.value}
              </span>
            </div>
          );
        })}
      </div>
    );
  };
  // const ROW_HEIGHT = 48; // safe for labels + dots
  const CLUB_COUNT = analytics.length;
  const ROW_HEIGHT =
    window.innerWidth < 768
      ? 24 + CLUB_COUNT * 10 // mobile
      : 32 + CLUB_COUNT * 8; // desktop

  const MIN_HEIGHT = 300;
  return (
    <div className="w-full pt-4">
      {/* Toast container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {loading ? <Loader /> : null}

      {/* Dropdown + Custom Legend Row */}
      <div className="flex flex-wrap items-center justify-center md:justify-between border-b border-gray-300 pb-4 mb-4 gap-3">
        <div className="w-full md:w-auto mb-2 md:mb-0 py-1">
          <Select
            isMulti
            options={clubData}
            value={selectedTeams}
            onChange={(value) => {
              const clubs = value as ClubOption[];
              if (clubs.length === 1) {
                toast.error("You need to select at least 2 clubs.");
                return;
              }
              if (clubs.length <= 4) {
                setSelectedTeams(clubs);
              } else {
                toast.error("You can select up to 4 clubs only.");
              }
            }}
            isClearable={false}
            components={{
              ClearIndicator: () => null,
            }}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "42px",
                alignItems: "flex-start",
              }),

              valueContainer: (base) => ({
                ...base,
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                maxHeight: "72px", // 👈 approx height for 2 rows
                overflowY: "auto",
                padding: "6px 8px",
              }),

              multiValue: (base) => ({
                ...base,
                backgroundColor: "#E6F0FF",
                borderRadius: "9999px",
                padding: "2px 6px",
                maxWidth: "100%",
                alignItems: "center",
              }),

              multiValueLabel: (base) => ({
                ...base,
                color: "#1E3A8A",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }),

              multiValueRemove: (base) => ({
                ...base,
                color: "#1E3A8A",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                border: "1px solid #1E3A8A",
                ":hover": {
                  backgroundColor: "#1E3A8A",
                  color: "#fff",
                },
              }),
            }}
          />
        </div>

        {/* Custom Legend */}
        <div className="flex flex-wrap gap-4">
          {analytics.map((club, index) => (
            <div key={club.club_id} className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded"
                style={
                  club.secondary_color
                    ? {
                        backgroundImage: `
            repeating-linear-gradient(
              45deg,
              ${club.hex_code},
              ${club.hex_code} 4px,
              ${club.secondary_color} 4px,
              ${club.secondary_color} 8px
            )
          `,
                      }
                    : {
                        backgroundColor: club.hex_code,
                      }
                }
              ></div>

              <span className="text-gray-800 text-sm">{club.club_name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div>
        <p className="text-center mb-2 font-medium">Individual Metric Index</p>
      </div>
      <ResponsiveContainer
        width="100%"
        height={Math.max(data.length * ROW_HEIGHT, MIN_HEIGHT)}
      >
        <BarChart
          data={data}
          layout="vertical"
          // barCategoryGap={12}
          // barGap={2}
          barGap={8}
          barCategoryGap={22}
          // barCategoryGap={window.innerWidth < 768 ? 18 : 12}
          // barGap={window.innerWidth < 768 ? 6 : 3}
          margin={{
            top: 10,
            right: 40,
            left: window.innerWidth < 768 ? 80 : 140,
            bottom: 20,
          }}
        >
          {/* ------------ STRIPED BAR PATTERNS ------------ */}
          <defs>
            {analytics.map((club) => (
              <pattern
                key={club.club_id}
                id={`stripe-${club.club_id}`}
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                {/* Base color */}
                <rect width="6" height="6" fill={club.hex_code} />

                {/* Stripe only if secondary color exists */}
                {club.secondary_color && (
                  <rect
                    width="3"
                    height="6"
                    fill={club.secondary_color}
                    opacity="1"
                  />
                )}
              </pattern>
            ))}
          </defs>

          {/* Grid */}
          <CartesianGrid vertical={true} horizontal={false} stroke="#ccc" />

          {/* X-axis */}
          <XAxis
            type="number"
            domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
            tick={{ fontSize: 12 }}
            axisLine
            tickLine
            stroke="#333"
            tickMargin={10}
            allowDecimals={false}
          />

          {/* Y-axis */}
          <YAxis
            dataKey="stat"
            type="category"
            tick={({ x, y, payload }) => (
              <text
                x={x}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                style={{
                  fontSize: window.innerWidth < 768 ? 10 : 14,
                  fill: "#333",
                }}
              >
                {payload.value}
              </text>
            )}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* <Legend /> */}
          <Legend
            verticalAlign="top"
            align="center"
            wrapperStyle={{
              position: "relative",
              marginBottom: 10,
            }}
          />

          {/* ------------ STRIPED BARS PER CLUB ------------ */}
          {analytics.map((club, index) => (
            <Bar
              key={club.club_id}
              dataKey={club.club_name}
              fill={`url(#stripe-${club.club_id})`} // ← striped bar fill
              radius={[5, 5, 5, 5]}
              barSize={window.innerWidth < 768 ? 4 : 6}
            >
              {/* Circle labels */}
              <LabelList
                dataKey={club.club_name}
                content={(props: any) => {
                  const { x = 0, y = 0, width = 0, height = 0, value } = props;
                  if (value == null) return null;

                  const cy = y + height / 2;

                  const OFFSET = window.innerWidth < 768 ? 4 : 6;
                  const cx = x + Math.max(width, 0) + OFFSET;

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={window.innerWidth < 768 ? 4 : 6}
                      fill={club.hex_code}
                    />
                  );
                }}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
